/**
 * Block 13 PR-G Phase 2 Step 5 — generate + download wrapper для contract PDF.
 *
 * Mirror Block 12 `generatePdf.ts` pattern. Public surface:
 *   - `buildContractFilename(draft)`: pure helper, derived имя файла.
 *   - `triggerContractPdfDownload(blob, filename)`: browser download trigger.
 *   - `generateAndDownloadContractPdf(draft, template)`: main wrapper.
 *
 * Filename pattern: `Договор_№{N}_{customerName}_{YYYY-MM-DD}.pdf`
 *   - Sanitizer заменяет `[\/:*?"<>|]+` и пробелы на `_` (Windows/macOS/Linux
 *     valid).
 *   - Пустые поля заменяются placeholder ('БезНомера' / 'БезКлиента') чтобы
 *     filename всегда был информативным.
 *
 * Side effects:
 *   - DOM: temporarily appends hidden `<a>` для click() trigger, removed через
 *     `anchor.remove()` + `URL.revokeObjectURL` в `finally`.
 *   - Network: один fetch per font weight на первом вызове (cached per session
 *     в renderContractToPdf module-level cache).
 *
 * Reference:
 *   - apps/web/src/features/calculator/quote/services/generatePdf.ts (pattern)
 *   - ADR-0025 §Q4
 *   - Block_13_PR_G_phase_0_planning.md Phase 2 Step 5
 */

import type { ContractDraft, ContractTemplate } from '@pomoshnik/shared';

import { renderContractToPdf } from './renderContractToPdf';

function formatDateForFilename(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Filename sanitizer — заменяет недопустимые для FS символы (`/`, `\`, `:`, ...)
 * + пробелы на `_`. Гарантирует валидное имя файла на Windows/macOS/Linux.
 * Возвращает '' если input пустой после trim.
 */
function sanitizePart(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .trim();
}

/**
 * Compose filename для договора. Pattern:
 *   `Договор_№{N}_{customerName}_{YYYY-MM-DD}.pdf`
 *
 * Empty contractNum → 'БезНомера' fallback. Empty customerName → omitted
 * (filename shrinks gracefully).
 */
export function buildContractFilename(draft: ContractDraft, now: Date = new Date()): string {
  const numPart = sanitizePart(draft.contractNum) || 'БезНомера';
  const customerPart = sanitizePart(draft.customerName);
  const datePart = formatDateForFilename(now);
  const parts = ['Договор', `№${numPart}`];
  if (customerPart !== '') parts.push(customerPart);
  parts.push(datePart);
  return `${parts.join('_')}.pdf`;
}

/**
 * Trigger browser download для PDF blob. Exported чтобы переиспользоваться в
 * `shareContractPdf.ts` (Step 6) — fallback path для browsers без `navigator.share`.
 * Сохраняет deferred-revoke pattern для Safari/FF (race fix).
 */
export function triggerContractPdfDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  let clickThrew = false;
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } catch (err) {
      clickThrew = true;
      throw err;
    } finally {
      anchor.remove();
    }
  } finally {
    // Revoke синхронно может отменить download в Safari/FF — браузер ещё не
    // успел запросить blob после click(). На throw в click() нет смысла
    // откладывать — download не начался.
    if (clickThrew) {
      URL.revokeObjectURL(url);
    } else {
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  }
}

/**
 * Generate PDF от ContractDraft + ContractTemplate и trigger browser download.
 * Errors propagated к caller — wrapping в try/catch + UI toast делает caller.
 */
export async function generateAndDownloadContractPdf(
  draft: ContractDraft,
  template: ContractTemplate,
): Promise<void> {
  const blob = await renderContractToPdf(draft, template);
  const filename = buildContractFilename(draft);
  triggerContractPdfDownload(blob, filename);
}
